import React from "react";
import { api } from "../../../shared/api";
import Modal from "../../../shared/ui/Modal";

type Choice = { id: string; name: string; market_site?: string };
type Preview = {
  remove_items: number;
  remove_logs: number;
  item_count: number;
  schedule_count: number;
  shops: Record<string, number>;
  groups: Record<string, number>;
  new_groups: string[];
  items: { index: number; name: string; original_shop: string; original_group: string; target_shop: string; target_group: string; schedule_count: number; has_error: boolean }[];
  errors: { index?: number; schedule_index?: number; message: string; fields?: Record<string, unknown> }[];
  available_shops: Choice[];
  available_groups: Choice[];
};
type PreviewResult = { job_id: string; token: string; preview: Preview };
type Job = { id: string; status: string; deleted_logs: number; item_count: number; error?: string };
type SavedSet = { id: string; name: string; item_count: number; is_auto_backup: boolean; created_at: string };
type Resolutions = Record<string, { shop_id?: string; group_id?: string; settings?: Record<string, unknown>; schedules?: Record<string, Record<string, unknown>> }>;

const errorText = (error: unknown) => {
  const detail = (error as { response?: { data?: { message?: string; detail?: string } } })?.response?.data;
  return detail?.message || detail?.detail || "요청에 실패했습니다. 다시 시도해 주세요.";
};

const exportErrorText = async (error: unknown) => {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (data instanceof Blob) {
    try {
      return errorText({ response: { data: JSON.parse(await data.text()) } });
    } catch { /* Fall back when the response is not JSON. */ }
  }
  return errorText(error);
};

const defaultSetNamePlaceholder = () => {
  const dateParts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Seoul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const part = (type: string) => dateParts.find((value) => value.type === type)?.value;
  return `상품목록 세트 (${part("year")}-${part("month")}-${part("day")})`;
};

export default function ItemTransferManager() {
  const [savedSets, setSavedSets] = React.useState<SavedSet[]>([]);
  const [savedSetLimit, setSavedSetLimit] = React.useState(20);
  const [setName, setSetName] = React.useState("");
  const [importName, setImportName] = React.useState("");
  const [previewSource, setPreviewSource] = React.useState("");
  const [importFile, setImportFile] = React.useState<File | null>(null);
  const [result, setResult] = React.useState<PreviewResult | null>(null);
  const [resolutions, setResolutions] = React.useState<Resolutions>({});
  const [settingDrafts, setSettingDrafts] = React.useState<Record<string, Record<string, string>>>({});
  const [scheduleDrafts, setScheduleDrafts] = React.useState<Record<string, Record<string, Record<string, string>>>>({});
  const [needsResolve, setNeedsResolve] = React.useState(false);
  const [confirmed, setConfirmed] = React.useState(false);
  const [confirmation, setConfirmation] = React.useState("");
  const [job, setJob] = React.useState<Job | null>(null);
  const seenJobId = React.useRef<string | null>(null);
  const [showProgress, setShowProgress] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [showSave, setShowSave] = React.useState(false);
  const [showImport, setShowImport] = React.useState(false);
  const [saveError, setSaveError] = React.useState("");
  const [importError, setImportError] = React.useState("");
  const [expandedItemIndex, setExpandedItemIndex] = React.useState<number | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");

  const loadSavedSets = React.useCallback(async () => {
    const response = await api.get("/items/transfer/sets/");
    setSavedSets(response.data.sets);
    setSavedSetLimit(response.data.max_sets);
  }, []);

  React.useEffect(() => {
    void loadSavedSets().catch((exc) => setError(errorText(exc)));
  }, [loadSavedSets]);

  React.useEffect(() => {
    let live = true;
    const poll = async () => {
      try {
        const active = await api.get("/items/transfer/import/jobs/active/");
        if (live && active.data.job) {
          const activeJob = active.data.job as Job;
          setJob(activeJob);
          if (seenJobId.current !== activeJob.id) {
            seenJobId.current = activeJob.id;
            setShowProgress(true);
          }
        }
      } catch { /* Keep the current state; the next poll can recover it. */ }
    };
    void poll();
    const timer = window.setInterval(poll, 3000);
    return () => { live = false; window.clearInterval(timer); };
  }, []);

  const jobId = job?.id;
  const jobStatus = job?.status;
  React.useEffect(() => {
    if (jobStatus === "완료") void loadSavedSets().catch((exc) => setError(errorText(exc)));
  }, [jobStatus, loadSavedSets]);
  React.useEffect(() => {
    if (!jobId || (jobStatus && ["완료", "실패"].includes(jobStatus))) return;
    let live = true;
    const poll = async () => {
      try {
        const response = await api.get(`/items/transfer/import/jobs/${jobId}/`);
        if (live) setJob(response.data);
      } catch { /* Keep polling while the job is active. */ }
    };
    const timer = window.setInterval(poll, 2000);
    return () => { live = false; window.clearInterval(timer); };
  }, [jobId, jobStatus]);

  const saveCurrentSet = async () => {
    if (!setName.trim()) return;
    setBusy(true); setSaveError("");
    try {
      await api.post("/items/transfer/sets/", { name: setName.trim() }, { timeout: 60000 });
      setSetName("");
      setShowSave(false);
      await loadSavedSets().catch((exc) => setError(errorText(exc)));
    } catch (exc) { setSaveError(errorText(exc)); }
    finally { setBusy(false); }
  };

  const closeSave = () => {
    if (busy) return;
    setShowSave(false);
    setSetName("");
    setSaveError("");
  };

  const closeImport = () => {
    if (busy) return;
    setShowImport(false);
    setImportFile(null);
    setImportName("");
    setImportError("");
  };

  const importSet = async () => {
    if (!importFile || !importName.trim()) return;
    setBusy(true); setImportError("");
    try {
      const form = new FormData();
      form.append("file", importFile);
      form.append("name", importName.trim());
      await api.post("/items/transfer/sets/upload/", form, { timeout: 60000 });
      setShowImport(false);
      setImportFile(null);
      setImportName("");
      await loadSavedSets().catch((exc) => setError(errorText(exc)));
    } catch (exc) { setImportError(errorText(exc)); }
    finally { setBusy(false); }
  };

  const deleteSavedSet = async (saved: SavedSet) => {
    if (!window.confirm(`"${saved.name}" 세트를 삭제하시겠습니까?`)) return;
    setBusy(true); setError("");
    try {
      await api.delete(`/items/transfer/sets/${saved.id}/`);
      if (previewSource === saved.name) { setResult(null); setPreviewSource(""); }
      await loadSavedSets();
    } catch (exc) { setError(errorText(exc)); }
    finally { setBusy(false); }
  };

  const downloadSavedSet = async (saved: SavedSet) => {
    setBusy(true); setError("");
    try {
      const response = await api.get(`/items/transfer/sets/${saved.id}/download/`, { responseType: "blob", timeout: 60000 });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `nprice-items-${saved.id}.nprice-items`;
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (exc) { setError(await exportErrorText(exc)); }
    finally { setBusy(false); }
  };

  const showPreview = (data: PreviewResult, source: string) => {
    setResult(data);
    setPreviewSource(source);
    setResolutions({});
    setSettingDrafts({});
    setScheduleDrafts({});
    setNeedsResolve(false);
    setConfirmed(false);
    setConfirmation("");
    setExpandedItemIndex(data.preview.errors.find((entry) => entry.index !== undefined)?.index ?? null);
  };

  const previewSavedSet = async (saved: SavedSet) => {
    setBusy(true); setError(""); setResult(null);
    try {
      const response = await api.post(`/items/transfer/sets/${saved.id}/preview/`, {}, { timeout: 60000 });
      showPreview(response.data, saved.name);
    } catch (exc) { setError(errorText(exc)); }
    finally { setBusy(false); }
  };

  const resolve = async () => {
    if (!result) return;
    setBusy(true); setError("");
    try {
      const nextResolutions: Resolutions = { ...resolutions };
      for (const [index, fields] of Object.entries(settingDrafts)) {
        const settings: Record<string, unknown> = {};
        for (const [field, value] of Object.entries(fields)) settings[field] = JSON.parse(value);
        nextResolutions[index] = { ...nextResolutions[index], settings };
      }
      for (const [index, schedules] of Object.entries(scheduleDrafts)) {
        const patches: Record<string, Record<string, unknown>> = {};
        for (const [scheduleIndex, fields] of Object.entries(schedules)) {
          patches[scheduleIndex] = {};
          for (const [field, value] of Object.entries(fields)) patches[scheduleIndex][field] = JSON.parse(value);
        }
        nextResolutions[index] = { ...nextResolutions[index], schedules: patches };
      }
      const response = await api.post("/items/transfer/import/resolve/", { job_id: result.job_id, resolutions: nextResolutions });
      setResult(response.data);
      setResolutions(nextResolutions);
      setNeedsResolve(false);
      setConfirmed(false);
      setConfirmation("");
    } catch (exc) { setError(exc instanceof SyntaxError ? "보완값을 올바른 JSON 형식으로 입력해 주세요." : errorText(exc)); }
    finally { setBusy(false); }
  };

  const commit = async () => {
    if (!result || !confirmed || confirmation !== "상품 교체" || result.preview.errors.length || needsResolve) return;
    setBusy(true); setError("");
    try {
      const response = await api.post("/items/transfer/import/commit/", {
        job_id: result.job_id, token: result.token, confirmation: "아이템 교체", acknowledged: confirmed,
      });
      setJob({ id: response.data.job_id, status: response.data.status, deleted_logs: 0, item_count: result.preview.item_count });
      seenJobId.current = response.data.job_id;
      setShowConfirm(false);
      setShowProgress(true);
      setResult(null);
    } catch (exc) { setError(errorText(exc)); }
    finally { setBusy(false); }
  };

  const updateField = (entry: Preview["errors"][number], field: string, value: string) => {
    const index = String(entry.index);
    if (entry.schedule_index === undefined) {
      setSettingDrafts((prev) => ({ ...prev, [index]: { ...prev[index], [field]: value } }));
    } else {
      const scheduleIndex = String(entry.schedule_index);
      setScheduleDrafts((prev) => ({
        ...prev,
        [index]: { ...prev[index], [scheduleIndex]: { ...prev[index]?.[scheduleIndex], [field]: value } },
      }));
    }
    setNeedsResolve(true);
  };

  const fieldValue = (entry: Preview["errors"][number], field: string, original: unknown) =>
    (entry.schedule_index === undefined
      ? settingDrafts[String(entry.index)]?.[field]
      : scheduleDrafts[String(entry.index)]?.[String(entry.schedule_index)]?.[field]) ?? JSON.stringify(original ?? null);

  const activeJob = Boolean(job && !["완료", "실패"].includes(job.status));
  const manualSetCount = savedSets.filter((saved) => !saved.is_auto_backup).length;

  return <div className="item-import-page" aria-label="상품목록 세트 백업/복구">
    {error && <div className="alert alert-danger" role="alert">{error}</div>}
    {job && <div className={`alert ${job.status === "실패" ? "alert-danger" : "alert-info"} d-flex justify-content-between align-items-center gap-3`} role="status">
      <span>
      상태: {job.status === "아이템 교체 중" ? "상품 교체 중" : job.status} · 로그 삭제 {job.deleted_logs}건 · 가져올 상품 {job.item_count}건
      {job.error && <div>{job.error}</div>}
      {job.status === "실패" && <div>삭제된 로그는 복구되지 않습니다.</div>}
      </span>
      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setShowProgress(true)}>진행 상태</button>
    </div>}
    <div className="data-card p-3 mb-3">
      <h6 className="mb-2">저장된 상품목록 세트</h6>
      <div className="d-flex flex-wrap gap-2 mb-3">
        <button type="button" className="btn btn-primary"
          disabled={busy || activeJob || manualSetCount >= savedSetLimit}
          onClick={() => setShowSave(true)}>현재 상품목록 저장</button>
        <button type="button" className="btn btn-outline-primary"
          disabled={busy || activeJob || manualSetCount >= savedSetLimit}
          onClick={() => setShowImport(true)}>파일 가져오기</button>
      </div>
      {savedSets.length > 0 ? <div className="table-scroll"><table className="table table-sm align-middle mb-0 text-center">
        <thead><tr><th>세트 이름</th><th>상품</th><th>저장일시</th><th>작업</th></tr></thead>
        <tbody>{savedSets.map((saved) => <tr key={saved.id}>
          <td>{saved.name}{saved.is_auto_backup && <span className="badge text-bg-secondary ms-2">자동 백업</span>}</td>
          <td>{saved.item_count}건</td>
          <td>{new Date(saved.created_at).toLocaleString("ko-KR")}</td>
          <td><div className="d-flex flex-wrap justify-content-center gap-2">
            <button type="button" className="btn btn-outline-primary btn-sm" disabled={busy || activeJob}
              onClick={() => void previewSavedSet(saved)}>미리보기</button>
            <button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy}
              onClick={() => void downloadSavedSet(saved)}>파일 저장</button>
            <button type="button" className="btn btn-outline-danger btn-sm" disabled={busy || activeJob}
              onClick={() => void deleteSavedSet(saved)}>삭제</button>
          </div></td>
        </tr>)}</tbody>
      </table></div> : <p className="mb-0">저장된 세트가 없습니다.</p>}
    </div>
    {result && <>
      <div className="data-card p-3 mb-3 d-flex justify-content-between align-items-center gap-3">
        <div>
          <h6 className="mb-0">불러올 상품목록 세트 미리보기: {previewSource}</h6>
          {result.preview.errors.length > 0 && <div className="text-warning mt-1">보완할 항목 {result.preview.errors.length}건</div>}
        </div>
        <button type="button" className="btn btn-danger flex-shrink-0" disabled={busy || activeJob || result.preview.errors.length > 0 || needsResolve} onClick={() => setShowConfirm(true)}>백업 세트 복구</button>
      </div>
      {result.preview.errors.some((entry) => entry.index === undefined) && <div className="alert alert-warning" role="alert">{result.preview.errors.filter((entry) => entry.index === undefined).map((entry) => entry.message).join(" · ")}</div>}
      <div className="data-card item-import-table-card">
        <div className="table-scroll">
          <table className="table table-sm align-middle mb-0">
            <thead><tr><th>순번</th><th>상태</th><th>상품명</th><th>원본 상점</th><th>연결 상점</th><th>원본 그룹</th><th>연결 그룹</th><th>스케줄</th><th>보완</th></tr></thead>
            <tbody>{result.preview.items.map((item) => <tr key={item.index}>
              <td>{item.index + 1}</td>
              <td><span className={`badge ${item.has_error ? "text-bg-warning" : "text-bg-success"}`}>{item.has_error ? "보완 필요" : "검증 완료"}</span></td>
              <td>{item.name || "-"}</td>
              <td>{item.original_shop || "-"}</td>
              <td>{item.target_shop || "확인 필요"}</td>
              <td>{item.original_group || "없음"}</td>
              <td>{item.target_group || (item.original_group ? "확인 필요" : "없음")}</td>
              <td>{item.schedule_count}건</td>
              <td>{item.has_error && <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setExpandedItemIndex(item.index)}>보완값 보기</button>}</td>
            </tr>)}</tbody>
          </table>
        </div>
        {expandedItemIndex !== null && result.preview.errors.some((entry) => entry.index === expandedItemIndex) && <div className="p-3 border-top">
          <h6>{expandedItemIndex + 1}번 상품 보완</h6>
          {result.preview.errors.filter((entry) => entry.index === expandedItemIndex).map((entry, index) => <div key={index} className="mb-3">
          {entry.index === undefined ? "전체" : `${entry.index + 1}번 상품${entry.schedule_index === undefined ? "" : ` · ${entry.schedule_index + 1}번 스케줄`}`}: {entry.message}
          {entry.index !== undefined && <div className="d-flex gap-2 mt-2">
            <select className="form-select" aria-label={`${entry.index + 1}번 상품 상점`} value={resolutions[String(entry.index)]?.shop_id || ""} onChange={(event) => { setResolutions((prev) => ({ ...prev, [String(entry.index)]: { ...prev[String(entry.index)], shop_id: event.target.value || undefined } })); setNeedsResolve(true); }}>
              <option value="">상점 선택</option>{result.preview.available_shops.map((choice) => <option key={choice.id} value={choice.id}>{choice.name} ({choice.market_site})</option>)}
            </select>
            <select className="form-select" aria-label={`${entry.index + 1}번 상품 그룹`} value={resolutions[String(entry.index)]?.group_id || ""} onChange={(event) => { setResolutions((prev) => ({ ...prev, [String(entry.index)]: { ...prev[String(entry.index)], group_id: event.target.value || undefined } })); setNeedsResolve(true); }}>
              <option value="">그룹 선택</option>{result.preview.available_groups.map((choice) => <option key={choice.id} value={choice.id}>{choice.name}</option>)}
            </select>
          </div>}
          {entry.index !== undefined && Object.entries(entry.fields || {}).map(([field, original]) => <div key={field} className="mt-2">
            <label className="form-label" htmlFor={`item-transfer-${entry.index}-${entry.schedule_index ?? "item"}-${field}`}>{field} · 원본: {JSON.stringify(original ?? null)}</label>
            <textarea id={`item-transfer-${entry.index}-${entry.schedule_index ?? "item"}-${field}`} className="form-control" rows={2} aria-label={`${Number(entry.index) + 1}번 상품 ${field} 보완값`} value={fieldValue(entry, field, original)} onChange={(event) => updateField(entry, field, event.target.value)} />
            <small>보완값을 JSON 형식으로 입력해 주세요.</small>
          </div>)}
          </div>)}
          <button type="button" className="btn btn-outline-primary" disabled={busy || !needsResolve} onClick={() => void resolve()}>선택 재검증</button>
        </div>}
      </div>
    </>}
    <Modal show={showSave} onHide={closeSave} size="sm" backdrop="static">
      <Modal.Header closeButton={!busy}><Modal.Title>현재 세트 저장</Modal.Title></Modal.Header>
      <Modal.Body>
        <label htmlFor="item-transfer-set-name" className="form-label">세트 이름</label>
        <input id="item-transfer-set-name" className="form-control" placeholder={defaultSetNamePlaceholder()} maxLength={100}
          value={setName} disabled={busy} onChange={(event) => setSetName(event.target.value)}
          onKeyDown={(event) => { if (event.key === "Enter" && setName.trim() && !busy) void saveCurrentSet(); }} />
        {saveError && <div className="alert alert-danger mt-3 mb-0" role="alert">{saveError}</div>}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-outline-secondary" disabled={busy} onClick={closeSave}>취소</button>
        <button type="button" className="btn btn-primary" disabled={busy || !setName.trim()} onClick={() => void saveCurrentSet()}>저장</button>
      </Modal.Footer>
    </Modal>
    <Modal show={showImport} onHide={closeImport} size="sm" backdrop="static">
      <Modal.Header closeButton={!busy}><Modal.Title>파일 가져오기</Modal.Title></Modal.Header>
      <Modal.Body>
        <label htmlFor="item-transfer-file" className="form-label">세트 파일</label>
        <input id="item-transfer-file" type="file" className="form-control mb-3" accept=".nprice-items"
          disabled={busy} onChange={(event) => setImportFile(event.target.files?.[0] ?? null)} />
        <label htmlFor="item-transfer-import-name" className="form-label">세트 이름</label>
        <input id="item-transfer-import-name" className="form-control" maxLength={100} placeholder="세트 이름"
          value={importName} disabled={busy} onChange={(event) => setImportName(event.target.value)} />
        {importError && <div className="alert alert-danger mt-3 mb-0" role="alert">{importError}</div>}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-outline-secondary" disabled={busy} onClick={closeImport}>취소</button>
        <button type="button" className="btn btn-primary" disabled={busy || !importFile || !importName.trim()} onClick={() => void importSet()}>가져오기</button>
      </Modal.Footer>
    </Modal>
    <Modal show={showConfirm && Boolean(result)} onHide={() => { if (!busy) setShowConfirm(false); }} size="lg" backdrop="static">
      <Modal.Header closeButton={!busy}><Modal.Title>백업 세트 복구 확인</Modal.Title></Modal.Header>
      <Modal.Body>
        {result && <p>현재 상품 {result.preview.remove_items}건과 로그 {result.preview.remove_logs}건을 정리하고, 백업 상품 {result.preview.item_count}건으로 교체합니다.</p>}
        <div className="alert alert-danger">세트를 복원하면 모든 사용자 로그가 먼저 영구 삭제된 후 현재 상품목록이 선택한 세트로 교체됩니다. 이후 상품 교체에 실패하더라도 삭제된 로그는 복구되지 않습니다. 현재 상품이 있으면 복원 직전에 자동 백업으로 저장됩니다.</div>
        <label className="d-block mb-3"><input type="checkbox" className="form-check-input me-2" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} />위 위험을 확인했습니다.</label>
        <label htmlFor="item-transfer-confirm" className="form-label">확인 문구: 상품 교체</label>
        <input id="item-transfer-confirm" className="form-control" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} />
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-outline-secondary" disabled={busy} onClick={() => setShowConfirm(false)}>취소</button>
        <button type="button" className="btn btn-danger" disabled={busy || !confirmed || confirmation !== "상품 교체" || !result || result.preview.errors.length > 0 || needsResolve} onClick={() => void commit()}>상품 교체 작업 등록</button>
      </Modal.Footer>
    </Modal>
    <Modal show={showProgress && Boolean(job)} onHide={() => setShowProgress(false)} size="sm">
      <Modal.Header closeButton><Modal.Title>백업 세트 복구 진행</Modal.Title></Modal.Header>
      <Modal.Body>
        <div role="status" aria-live="polite">{job?.status === "아이템 교체 중" ? "상품 교체 중" : job?.status} · 로그 삭제 {job?.deleted_logs ?? 0}건</div>
        {job?.status === "실패" && <div className="alert alert-danger mt-3">{job.error || "상품 교체에 실패했습니다."} 삭제된 로그는 복구되지 않습니다.</div>}
        {job?.status === "완료" && <div className="alert alert-success mt-3">상품 교체가 완료되었습니다.</div>}
        {activeJob && <p className="mb-0 mt-2">모달을 닫아도 작업 상태를 이 화면에서 다시 확인할 수 있습니다.</p>}
      </Modal.Body>
      <Modal.Footer><button type="button" className="btn btn-outline-secondary" onClick={() => setShowProgress(false)}>닫기</button></Modal.Footer>
    </Modal>
  </div>;
}
